import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { selectCompanies, selectSelectedCompanyId } from '@store/selectors/company.selectors';
import { selectSelectedWebshopId, selectWebshops } from '@store/selectors/webshop.selectors';
import { selectCompany } from '@store/actions/company.actions';
import { selectWebshop } from '@store/actions/webshop.actions';
import { toggleMenu } from '@store/actions/component.actions';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { SelectModule } from 'primeng/select';

import { AppButtonComponent } from '@components/wrappers/button/button.component';
import { environment } from '@environment/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, take } from 'rxjs';

@Component({
  selector: 'app-topbar',
  imports: [
    RouterLink,
    FormsModule,
    SelectModule,
    AppButtonComponent,
    AsyncPipe
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit {
  private readonly store = inject(Store);
  protected companies$ = this.store.select(selectCompanies);
  protected selectedCompanyId$ = this.store.select(selectSelectedCompanyId);
  protected webshops$ = this.store.select(selectWebshops);
  protected selectedWebshopId$ = this.store.select(selectSelectedWebshopId);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly destroyRef = inject(DestroyRef);

  protected webshopSelectDisabled$ = combineLatest([this.companies$, this.selectedCompanyId$]).pipe(
    map(([companies, selectedCompanyId]) => companies.length > 1 && !selectedCompanyId)
  );

  ngOnInit() {
    const lastCompanyId = localStorage.getItem('lastSelectedCompanyId');
    const lastWebshopId = localStorage.getItem('lastSelectedWebshopId');

    combineLatest([this.companies$, this.selectedCompanyId$])
      .pipe(
        filter(([companies]) => companies.length > 0),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe(([companies, selectedId]) => {
        if (!selectedId) {
          if (companies.length === 1) {
            this.onCompanyChange(companies[0].id);
          } else if (lastCompanyId && companies.find(c => c.id === lastCompanyId)) {
            this.onCompanyChange(lastCompanyId);
          }
        }
      });

    combineLatest([this.webshops$, this.selectedWebshopId$])
      .pipe(
        filter(([webshops]) => webshops.length > 0),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe(([webshops, selectedId]) => {
        if (!selectedId) {
          if (webshops.length === 1) {
            this.onWebshopChange(webshops[0].id ?? null);
          } else if (lastWebshopId && webshops.find(w => w.id === lastWebshopId)) {
            this.onWebshopChange(lastWebshopId);
          }
        }
      });
  }

  logout() {
    if (window.localStorage) {
      window.localStorage.clear();
    }
    window.location.href = environment.aws.logoutUrl;
  }

  protected onCompanyChange(companyId: string | null) {
    this.store.dispatch(selectCompany({payload: companyId}));
    if (companyId) {
      localStorage.setItem('lastSelectedCompanyId', companyId);
    } else {
      localStorage.removeItem('lastSelectedCompanyId');
    }
  }

  protected onWebshopChange(webshopId: string | null) {
    this.store.dispatch(selectWebshop({payload: webshopId}));
    if (webshopId) {
      localStorage.setItem('lastSelectedWebshopId', webshopId);
    } else {
      localStorage.removeItem('lastSelectedWebshopId');
    }
  }

  protected toggleMenu() {
    this.store.dispatch(toggleMenu());
  }
}
